// Esri operational adapter. Wraps an ArcGIS Maps SDK `MapView` that the
// consumer already created and exposes the structural `OperationalAdapter`
// contract from `src/adapters/types.ts`.
//
// Adapter encapsulation rule: this file must NOT import from `@arcgis/core`.
// The SDK is a peer dependency the consumer app brings; we duck-type the
// view at runtime so TypeScript never demands that `@arcgis/core` be
// resolvable from FuseSimple's own `node_modules`. Every Esri type we need
// is expressed as a structural `DuckMapView` interface below.
//
// The adapter owns:
//   - Sibling `<div>` creation for the MapLibre basemap (CSS stacking)
//   - View-state subscription (center / zoom / rotation) with rAF batching
//   - Click / hover passthrough detection via `hitTest`
//   - OSM attribution injection into the Esri Attribution widget

import { DebugLogger } from "../debug-logger";
import type { GeoScreenPoint, OperationalAdapter, ViewState } from "./types";

// ---------------------------------------------------------------------------
// Duck-typed subset of `@arcgis/core/views/MapView`. We check for these
// members at construction time and throw early with a clear message if the
// consumer didn't pass a real MapView. Keeping this internal avoids leaking
// Esri types through the adapter layer.
// ---------------------------------------------------------------------------

/** Minimal Point shape returned by `view.center` and `view.toMap()`. */
interface DuckPoint {
  longitude: number;
  latitude: number;
}

/** Minimal shape of the Esri `view.hitTest()` result. */
interface DuckHitTestResult {
  results: unknown[];
}

/** What `reactiveUtils.watch()` and `view.on()` return. */
interface DuckHandle {
  remove(): void;
}

/**
 * Duck-typed subset of `@arcgis/core/core/reactiveUtils`. Resolved
 * dynamically at runtime (via `$arcgis.import` for CDN users or
 * `import()` for npm users) so FuseSimple never has a static dependency
 * on `@arcgis/core`.
 */
export interface DuckReactiveUtils {
  watch(
    getValue: () => unknown,
    callback: (value: unknown) => void,
    options?: { initial?: boolean; sync?: boolean; once?: boolean },
  ): DuckHandle;
}

/** The subset of MapView we actually touch at runtime. */
interface DuckMapView {
  container: HTMLDivElement;
  center: DuckPoint;
  zoom: number;
  rotation: number;
  size: [number, number];
  background: unknown;
  on(type: string, handler: (event: unknown) => void): DuckHandle;
  hitTest(screenPoint: { x: number; y: number }): Promise<DuckHitTestResult>;
  toMap(screenPoint: { x: number; y: number }): DuckPoint;
}

/** Shape of a click event from `view.on("click", ...)`. */
interface DuckClickEvent {
  x: number;
  y: number;
  mapPoint: DuckPoint;
}

/** Shape of a pointer-move event from `view.on("pointer-move", ...)`. */
interface DuckPointerMoveEvent {
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

export interface EsriAdapterOptions {
  /**
   * An `@arcgis/core` MapView instance. Typed as `unknown` so FuseSimple
   * never forces the consumer to satisfy TypeScript's module resolution for
   * `@arcgis/core` at compile time. The adapter narrows via duck-type
   * checks at construction and throws if the object doesn't quack.
   */
  view: unknown;

  /**
   * `reactiveUtils` from `@arcgis/core/core/reactiveUtils`. Resolved
   * dynamically by `FuseSimple.create()` and passed in so the adapter
   * uses the modern `reactiveUtils.watch()` API instead of the deprecated
   * `view.watch()`.
   */
  reactiveUtils: DuckReactiveUtils;

  /**
   * Optional logger. When omitted a no-op instance is created so call
   * sites never need a null check. `FuseSimple.create()` passes its own
   * logger so adapter output lands under the same `?debug=` flag.
   */
  logger?: DebugLogger;
}

/**
 * CSS class applied to the basemap sibling div so it can be identified in
 * the DOM inspector and targeted by integration tests.
 */
const BASEMAP_CONTAINER_CLASS = "fusesimple-basemap-container";

/**
 * CSS selector for the Esri Attribution widget's "Powered by Esri" element.
 * Our credits are injected as a sibling before this element so they stay
 * pinned on the right side of the attribution bar (like "Powered by Esri"
 * itself) instead of getting buried in the scrolling sources text.
 */
const ATTRIBUTION_POWERED_BY_SELECTOR = ".esri-attribution__powered-by";

/**
 * Operational adapter wrapping an ArcGIS Maps SDK `MapView`. Duck-types
 * the view at runtime so `@arcgis/core` is never imported (it's a peer
 * dep the consumer brings). Owns the sibling `<div>` for the basemap,
 * view-state subscription, click/hover passthrough via `hitTest`, and
 * OSM attribution injection.
 */
export class EsriOperationalAdapter implements OperationalAdapter {
  private readonly mapView: DuckMapView;
  private readonly reactiveUtils: DuckReactiveUtils;
  private readonly logger: DebugLogger;

  /** Cached sibling div — created once on first `getBasemapContainer()`. */
  private basemapContainer: HTMLElement | null = null;

  /** MutationObservers from addAttribution(), disconnected on destroy(). */
  private readonly attributionObservers: MutationObserver[] = [];

  constructor(options: EsriAdapterOptions) {
    this.mapView = narrowView(options.view);
    this.reactiveUtils = options.reactiveUtils;
    this.logger =
      options.logger ??
      new DebugLogger({
        name: "FUSESIMPLE",
        features: ["LIFECYCLE", "SYNC", "PASSTHROUGH", "EVENTS", "ATTRIBUTION"],
      });
  }

  // -----------------------------------------------------------------------
  // View state subscription
  // -----------------------------------------------------------------------

  /**
   * Subscribe to Esri view state changes (center, zoom, rotation).
   * Uses microtask batching: the three `reactiveUtils.watch()` calls
   * (center, zoom, rotation) fire synchronously with `{ sync: true }`,
   * so during a pinch-zoom both center and zoom change in the same
   * synchronous block. A dirty flag + `queueMicrotask()` coalesces
   * them into a single `handler()` call (and therefore a single
   * MapLibre `jumpTo()` + `redraw()`). The microtask runs after the
   * synchronous burst but before the browser paints — zero frame delay.
   *
   * Returns an unsubscribe function.
   */
  onViewStateChange(handler: (state: ViewState) => void): () => void {
    const view = this.mapView;
    const logger = this.logger;
    const ru = this.reactiveUtils;

    // Microtask batching: multiple synchronous watcher fires within the
    // same Esri property-change cycle are coalesced into one handler call.
    let dirty = false;

    const onWatch = () => {
      if (dirty) return;
      dirty = true;
      queueMicrotask(() => {
        dirty = false;

        const state: ViewState = {
          center: {
            lng: view.center.longitude,
            lat: view.center.latitude,
          },
          zoom: view.zoom,
          // Sign-flip: Esri rotation is CW-positive, MapLibre bearing is
          // CW-positive in the opposite direction. `bearing = -rotation`.
          // This conversion happens here, once, so the basemap adapter and
          // the sync core never need to know about the convention difference.
          bearing: -view.rotation,
        };

        logger.log("SYNC", {
          message: "view state emit (batched)",
          center: state.center,
          zoom: state.zoom,
          bearing: state.bearing,
        });

        handler(state);
      });
    };

    // `sync: true` fires the callback synchronously when a tracked
    // property changes. The microtask batch above ensures we only call
    // handler() once per synchronous burst, not once per property.
    const opts = { sync: true };
    const h1 = ru.watch(() => view.center, onWatch, opts);
    const h2 = ru.watch(() => view.zoom, onWatch, opts);
    const h3 = ru.watch(() => view.rotation, onWatch, opts);

    logger.log("LIFECYCLE", { message: "view state watch started" });

    return () => {
      h1.remove();
      h2.remove();
      h3.remove();
      logger.log("LIFECYCLE", { message: "view state watch stopped" });
    };
  }

  // -----------------------------------------------------------------------
  // Click passthrough
  // -----------------------------------------------------------------------

  /**
   * Subscribe to click events that pass through Esri (i.e. clicks that
   * don't hit any Esri feature). Uses `hitTest` to decide: if there are
   * zero results the click is forwarded to `handler` with screen and
   * geographic coordinates so the caller can query MapLibre features.
   * Returns an unsubscribe function that removes the listener.
   */
  onPassthroughClick(handler: (point: GeoScreenPoint) => void): () => void {
    const view = this.mapView;
    const logger = this.logger;

    const handle = view.on("click", (rawEvent: unknown) => {
      const event = rawEvent as DuckClickEvent;
      view
        .hitTest({ x: event.x, y: event.y })
        .then((response) => {
          if (response.results.length > 0) {
            // Esri feature was hit — not a passthrough.
            return;
          }
          const point: GeoScreenPoint = {
            x: event.x,
            y: event.y,
            lng: event.mapPoint.longitude,
            lat: event.mapPoint.latitude,
          };
          logger.log("PASSTHROUGH", {
            message: "click passthrough",
            point,
          });
          handler(point);
        })
        .catch(() => {
          // hitTest can reject if the view is destroyed mid-click.
          // Silently ignore — there's nobody left to receive the event.
        });
    });

    logger.log("LIFECYCLE", { message: "click passthrough listener started" });

    return () => {
      handle.remove();
      logger.log("LIFECYCLE", {
        message: "click passthrough listener stopped",
      });
    };
  }

  // -----------------------------------------------------------------------
  // Hover passthrough (rAF-throttled)
  // -----------------------------------------------------------------------

  /**
   * Subscribe to pointer-move events that pass through Esri. Throttled
   * to one `hitTest` per animation frame via `requestAnimationFrame` to
   * avoid flooding the GPU with hit-test work during fast mouse movement.
   * When `hitTest` returns zero results the latest pointer position is
   * converted to geographic coordinates via `view.toMap()` and forwarded
   * to `handler`. Returns an unsubscribe function that cancels the
   * pending rAF (if any) and removes the listener.
   */
  onPassthroughHover(handler: (point: GeoScreenPoint) => void): () => void {
    const view = this.mapView;
    const logger = this.logger;

    let pendingEvent: DuckPointerMoveEvent | null = null;
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      const event = pendingEvent;
      if (!event) return;
      pendingEvent = null;

      view
        .hitTest({ x: event.x, y: event.y })
        .then((response) => {
          if (response.results.length > 0) return;

          // pointer-move events don't carry mapPoint — convert via toMap.
          const mapPoint = view.toMap({ x: event.x, y: event.y });
          if (!mapPoint) return;

          const point: GeoScreenPoint = {
            x: event.x,
            y: event.y,
            lng: mapPoint.longitude,
            lat: mapPoint.latitude,
          };
          logger.log("PASSTHROUGH", {
            message: "hover passthrough",
            point,
          });
          handler(point);
        })
        .catch(() => {
          // View destroyed mid-hover — ignore.
        });
    };

    const handle = view.on("pointer-move", (rawEvent: unknown) => {
      pendingEvent = rawEvent as DuckPointerMoveEvent;
      if (rafId === null) {
        rafId = requestAnimationFrame(flush);
      }
    });

    logger.log("LIFECYCLE", {
      message: "hover passthrough listener started",
    });

    return () => {
      handle.remove();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pendingEvent = null;
      logger.log("LIFECYCLE", {
        message: "hover passthrough listener stopped",
      });
    };
  }

  // -----------------------------------------------------------------------
  // Attribution
  // -----------------------------------------------------------------------

  /**
   * Inject attribution text (e.g. "© OpenStreetMap") into the Esri
   * Attribution widget as a pinned element next to "Powered by Esri".
   *
   * The attribution bar is a flex row: `__sources` (left, truncates)
   * and `__powered-by` (right, pinned). Injecting into `__sources`
   * buries our text behind long layer attributions. Instead we insert
   * a new `<span>` before `__powered-by` with `flex-shrink: 0` so it
   * stays visible regardless of how long the sources text gets.
   *
   * Installs a `MutationObserver` to re-inject after the widget
   * re-renders (which happens when Esri layers change). No-ops
   * silently if the attribution widget isn't found in the DOM.
   */
  addAttribution(text: string): void {
    const container = this.mapView.container;
    if (!container) return;

    // Find the "Powered by Esri" element — our credits go before it.
    const poweredByEl = container.querySelector(ATTRIBUTION_POWERED_BY_SELECTOR);
    if (!poweredByEl) {
      this.logger.log("ATTRIBUTION", {
        message: "attribution widget not found in DOM, skipping injection",
      });
      return;
    }

    const parentEl = poweredByEl.parentElement;
    if (!parentEl) return;

    // Build a unique class name so we can find/de-dupe our injected spans.
    const className = "fusesimple-attribution";

    const inject = () => {
      // De-duplicate: check if this exact text is already injected.
      const existing = parentEl.querySelectorAll(`.${className}`);
      for (const el of existing) {
        if (el.textContent?.includes(text)) return;
      }

      const span = document.createElement("span");
      span.className = className;
      span.style.cssText =
        "flex-shrink: 0; white-space: nowrap; padding-left: 6px; opacity: 0.75; font-size: inherit;";
      span.textContent = `| ${text}`;

      parentEl.insertBefore(span, poweredByEl);

      this.logger.log("ATTRIBUTION", {
        message: "attribution injected (pinned)",
        text,
      });
    };

    inject();

    // Watch for re-renders that wipe our injected elements.
    const observer = new MutationObserver(() => {
      // Check if our span is still in the DOM.
      const existing = parentEl.querySelectorAll(`.${className}`);
      let found = false;
      for (const el of existing) {
        if (el.textContent?.includes(text)) {
          found = true;
          break;
        }
      }
      if (!found) {
        inject();
        this.logger.log("ATTRIBUTION", {
          message: "attribution re-injected after widget re-render",
          text,
        });
      }
    });
    observer.observe(parentEl, { childList: true, subtree: true });
    this.attributionObservers.push(observer);
  }

  // -----------------------------------------------------------------------
  // Basemap container
  // -----------------------------------------------------------------------

  /**
   * Return (or lazily create) the sibling `<div>` that MapLibre mounts
   * into. The div is inserted as the first child of the Esri view's
   * container so it renders behind the Esri canvas via z-index stacking.
   * `pointer-events: none` ensures all input routes through Esri.
   * The element is cached after the first call. Throws if
   * `view.container` is not set.
   */
  getBasemapContainer(): HTMLElement {
    if (this.basemapContainer) return this.basemapContainer;

    const viewContainer = this.mapView.container;
    if (!viewContainer) {
      throw new Error(
        "EsriOperationalAdapter.getBasemapContainer(): view.container is not set. " +
          "Make sure you pass a MapView with a valid container.",
      );
    }

    // Create the sibling div that MapLibre will mount into. It sits
    // behind the Esri canvas via z-index stacking within the same
    // parent container. `pointer-events: none` ensures all input
    // routes through Esri — MapLibre is purely visual.
    // See `FuseSimple_v1_Decisions.md` §1 (DOM Strategy).
    const div = document.createElement("div");
    div.className = BASEMAP_CONTAINER_CLASS;
    div.style.position = "absolute";
    div.style.inset = "0";
    div.style.zIndex = "0";
    div.style.pointerEvents = "none";
    div.style.overflow = "hidden";

    // Insert as the first child so it renders behind Esri's canvas.
    viewContainer.insertBefore(div, viewContainer.firstChild);

    this.basemapContainer = div;
    this.logger.log("LIFECYCLE", {
      message: "basemap container created",
      className: BASEMAP_CONTAINER_CLASS,
    });

    return div;
  }

  // -----------------------------------------------------------------------
  // Teardown
  // -----------------------------------------------------------------------

  /**
   * Clean up DOM and observers created by this adapter. Disconnects
   * attribution MutationObservers and removes the basemap container div
   * from the Esri view. Safe to call multiple times.
   */
  destroy(): void {
    // Disconnect attribution observers so they stop re-injecting text
    // into the Esri widget after FuseSimple is torn down.
    for (const observer of this.attributionObservers) {
      observer.disconnect();
    }
    this.attributionObservers.length = 0;

    if (this.basemapContainer) {
      this.basemapContainer.remove();
      this.basemapContainer = null;
      this.logger.log("LIFECYCLE", {
        message: "basemap container removed",
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Duck-type validation
// ---------------------------------------------------------------------------

/**
 * Narrow `unknown` to `DuckMapView`. Throws with a clear message if the
 * object doesn't look like a MapView so the consumer gets feedback at
 * construction time rather than a cryptic `undefined is not a function`
 * deep inside a callback.
 */
function narrowView(view: unknown): DuckMapView {
  if (!view || typeof view !== "object") {
    throw new Error(
      `EsriOperationalAdapter: expected an @arcgis/core MapView instance, got ${view === null ? "null" : typeof view}`,
    );
  }

  const v = view as Record<string, unknown>;

  // Check the minimum set of members we actually call at runtime.
  const required: string[] = ["container", "center", "zoom", "rotation", "on", "hitTest", "toMap"];

  for (const key of required) {
    if (!(key in v)) {
      throw new Error(
        `EsriOperationalAdapter: the view is missing "${key}". Make sure you pass an @arcgis/core MapView, not a plain object.`,
      );
    }
  }

  return view as DuckMapView;
}
